import {ReportFilterParamSummary, ReportType} from '@gatewee/types/report';
import {TaskLabelTranslateFn} from './get-task-label';

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

function smartParseValue(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  return value;
}

function setByPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const parts = path.split('.').filter(Boolean);
  let cur: Record<string, unknown> = obj;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    const isLast = i === parts.length - 1;

    if (isLast) {
      cur[part] = value;
      continue;
    }

    const next = cur[part];
    if (typeof next !== 'object' || next === null || Array.isArray(next)) {
      cur[part] = {};
    }
    cur = cur[part] as Record<string, unknown>;
  }
}

function expandReportFilterDotPaths(
  record: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const [key, raw] of Object.entries(record)) {
    const value = smartParseValue(raw);
    const path = key.startsWith('options.')
      ? key.slice('options.'.length)
      : key;

    if (path.includes('.')) {
      setByPath(out, path, value);
    } else {
      out[path] = value;
    }
  }

  return out;
}

/** Report filters are stored as `{ where, startDate, endDate, search }` — not `{ options: { where } }`. */
export function normalizeReportFilterPayload(
  filters: unknown,
): Record<string, unknown> | undefined {
  const current = smartParseValue(filters);
  const root = asRecord(current);
  if (!root) {
    return undefined;
  }

  const unwrapped = asRecord(root['filters']) ?? root;
  const expanded = expandReportFilterDotPaths(unwrapped);
  return hoistOptionsLayer(expanded);
}

/** Legacy payloads may still nest under `options`; hoist to the report filter shape. */
function hoistOptionsLayer(
  record: Record<string, unknown>,
): Record<string, unknown> {
  const nested = asRecord(record['options']);
  if (!nested) {
    return record;
  }

  const rest = {...record};
  delete rest['options'];
  return {...rest, ...nested};
}

function pickScalarId(value: unknown): string | number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    return value.trim();
  }

  const record = asRecord(value);
  const id = record?.['id'];
  if (typeof id === 'number' && Number.isFinite(id)) {
    return id;
  }
  if (typeof id === 'string' && id.trim() !== '') {
    return id.trim();
  }

  return undefined;
}

function pickFilterString(
  where: Record<string, unknown> | undefined,
  payload: Record<string, unknown>,
  keys: string[],
): string | undefined {
  const direct =
    (where ? pickString(where, keys) : undefined) ?? pickString(payload, keys);

  if (direct) {
    return direct;
  }

  for (const key of keys) {
    const value = where?.[key] ?? payload[key];
    if (Array.isArray(value)) {
      const parts = value
        .map(item => (typeof item === 'string' ? item.trim() : String(item)))
        .filter(Boolean);
      if (parts.length > 0) {
        return parts.join(', ');
      }
    }
  }

  return undefined;
}

function pickString(
  source: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

function formatFilterDate(value: string): string | undefined {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function translateEnum(
  translate: TaskLabelTranslateFn,
  prefix: string,
  value: string,
): string {
  const key = `${prefix}.${value}`;
  const translated = translate(key);
  return translated !== key ? translated : value;
}

function statusEnumPrefix(reportType?: ReportType): string {
  switch (reportType) {
    case 'transactions':
      return 'enums.transactionStatus';
    case 'withdrawals':
      return 'enums.withdrawalStatus';
    default:
      return 'enums.invoiceStatus';
  }
}

/** Build human-readable filter entries from stored report filter payload. */
export function summarizeReportFilters(
  filters: unknown,
  translate: TaskLabelTranslateFn,
  reportType?: ReportType,
): ReportFilterParamSummary[] {
  const payload = normalizeReportFilterPayload(filters);
  if (!payload) {
    return [];
  }

  const where = asRecord(payload['where']);
  const entries: ReportFilterParamSummary[] = [];

  const search = pickString(payload, ['search']);
  if (search) {
    entries.push({
      label: translate('common.filters.search'),
      value: search,
    });
  }

  const status = pickFilterString(where, payload, ['status']);
  if (status) {
    entries.push({
      label: translate('common.filters.status'),
      value: translateEnum(translate, statusEnumPrefix(reportType), status),
    });
  }

  const transactionType =
    pickFilterString(where, payload, ['type', 'transactionType']) ??
    pickString(payload, ['transactionType']);
  if (reportType === 'transactions' && transactionType) {
    entries.push({
      label: translate('features.transactions.table.columns.type'),
      value: translateEnum(translate, 'enums.transactionType', transactionType),
    });
  }

  if (reportType === 'provider_transactions' && transactionType) {
    entries.push({
      label: translate(
        'features.recipients.providerTransactions.report.transactionType',
      ),
      value: transactionType,
    });
  }

  const provider = pickFilterString(where, payload, ['provider']);
  if (reportType === 'provider_transactions' && provider) {
    entries.push({
      label: translate('common.filters.canonicalProvider'),
      value: provider,
    });
  }

  const onlyConfirmed = payload['onlyConfirmed'];
  if (reportType === 'provider_transactions' && onlyConfirmed === true) {
    entries.push({
      label: translate(
        'features.recipients.providerTransactions.report.onlyConfirmed',
      ),
      value: translate('common.status.yes'),
    });
  }

  if (reportType === 'provider_transactions') {
    for (const key of [
      'e2eId',
      'transactionId',
      'externalId',
      'idempotencyKey',
    ] as const) {
      const value = pickString(payload, [key]);
      if (value) {
        entries.push({
          label: translate(
            `features.recipients.providerTransactions.search.${key}`,
          ),
          value,
        });
      }
    }
  }

  const clientId =
    pickScalarId(where?.['client']) ??
    pickScalarId(where?.['client.id']) ??
    pickScalarId(payload['client']);
  if (clientId !== undefined) {
    entries.push({
      label: translate('common.filters.client'),
      value: `#${clientId}`,
    });
  }

  const recipientId =
    pickScalarId(where?.['recipient']) ??
    pickScalarId(where?.['recipient.id']) ??
    pickScalarId(where?.['recipientId']) ??
    pickScalarId(payload['recipient']);
  if (recipientId !== undefined) {
    entries.push({
      label: translate('common.filters.recipient'),
      value: `#${recipientId}`,
    });
  }

  const paymentMethod = pickFilterString(where, payload, ['paymentMethod']);
  if (paymentMethod) {
    entries.push({
      label: translate('common.filters.paymentMethod'),
      value: translateEnum(translate, 'enums.paymentMethod', paymentMethod),
    });
  }

  const startDate = pickString(payload, ['startDate']);
  const endDate = pickString(payload, ['endDate']);

  if (startDate && endDate) {
    entries.push({
      label: translate('common.filters.period.label'),
      value: translate('features.tasks.labels.dateRange', {
        start: formatFilterDate(startDate) ?? startDate,
        end: formatFilterDate(endDate) ?? endDate,
      }),
    });
  } else if (startDate) {
    entries.push({
      label: translate('common.filters.period.label'),
      value: translate('features.tasks.labels.fromDate', {
        date: formatFilterDate(startDate) ?? startDate,
      }),
    });
  } else if (endDate) {
    entries.push({
      label: translate('common.filters.period.label'),
      value: translate('features.tasks.labels.untilDate', {
        date: formatFilterDate(endDate) ?? endDate,
      }),
    });
  }

  return entries;
}

export function formatReportFiltersBrief(
  entries: ReportFilterParamSummary[],
  translate: TaskLabelTranslateFn,
  maxParts = 2,
): string {
  if (entries.length === 0) {
    return translate('features.reports.table.filtersSummary.none');
  }

  const parts = entries
    .slice(0, maxParts)
    .map(entry => `${entry.label}: ${entry.value}`);

  if (entries.length > maxParts) {
    parts.push(`+${entries.length - maxParts}`);
  }

  return parts.join(' · ');
}

export function formatReportFiltersFull(
  entries: ReportFilterParamSummary[],
  translate: TaskLabelTranslateFn,
): string {
  if (entries.length === 0) {
    return translate('features.reports.table.filtersSummary.none');
  }

  return entries.map(entry => `${entry.label}: ${entry.value}`).join(' · ');
}
